import { useState, useEffect } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Separator } from "@/components/ui/separator";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Calendar } from "@/components/ui/calendar";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { CalendarIcon, Clock } from "lucide-react";
import { cn } from "@/lib/utils";
import { format } from "date-fns";
import { useBloodGlucoseSourceTypes } from "@/hooks/Trackers/useBloodGlucoseSourceTypes";
import {
  useBloodPressurePositions,
  useBloodPressureLocations,
} from "@/hooks/Trackers/useBloodPressureData";
import { apiService } from "@/lib/api";
import type {
  AddTrackerEntryModalProps,
  TrackerFieldConfig,
  TrackerEntryForm,
  BloodGlucoseSourceType,
} from "@/types/tracker";
import type { User } from "@/types";

// Validation response type
interface ValidationResponse {
  category: "error" | "warning" | "info";
  title: string;
  description: string;
  action?: {
    label: string;
    target: string;
  };
}

// Field configurations for different tracker types
const getFieldConfigs = (metricId: string): TrackerFieldConfig[] => {
  switch (metricId) {
    case "heart-rate":
      return [
        {
          id: "value",
          label: "Heart Rate",
          type: "number",
          placeholder: "Enter bpm here",
          unit: "bpm",
          required: true,
          min: 30,
          max: 250,
          step: 1,
        },
      ];

    case "blood-pressure":
      return [
        {
          id: "systolic",
          label: "Systolic Pressure",
          type: "number",
          placeholder: "Enter systolic value",
          unit: "mmHg",
          required: true,
          min: 70,
          max: 250,
          step: 1,
        },
        {
          id: "diastolic",
          label: "Diastolic Pressure",
          type: "number",
          placeholder: "Enter diastolic value",
          unit: "mmHg",
          required: true,
          min: 40,
          max: 150,
          step: 1,
        },
      ];

    case "blood-glucose":
      return [
        {
          id: "value",
          label: "Reading",
          type: "number",
          placeholder: "Enter mg/dl here",
          unit: "mg/dl",
          required: true,
          min: 50,
          max: 500,
          step: 1,
        },
      ];

    case "temperature":
      return [
        {
          id: "value",
          label: "Temperature",
          type: "number",
          placeholder: "Enter temperature",
          unit: "°F",
          required: true,
          min: 90,
          max: 110,
          step: 0.1,
        },
      ];

    case "weight":
      return [
        {
          id: "value",
          label: "Weight",
          type: "number",
          placeholder: "Enter weight",
          unit: "kg",
          required: true,
          min: 30,
          max: 300,
          step: 0.1,
        },
      ];

    case "waist-size":
      return [
        {
          id: "value",
          label: "Waist Measurement",
          type: "number",
          placeholder: "Enter measurement",
          unit: "cm",
          required: true,
          min: 50,
          max: 200,
          step: 0.1,
        },
      ];

    case "hip":
      return [
        {
          id: "value",
          label: "Hip Measurement",
          type: "number",
          placeholder: "Enter measurement",
          unit: "cm",
          required: true,
          min: 60,
          max: 200,
          step: 0.1,
        },
      ];

    case "spo2":
      return [
        {
          id: "value",
          label: "SpO2 Level",
          type: "number",
          placeholder: "Enter percentage",
          unit: "%",
          required: true,
          min: 70,
          max: 100,
          step: 1,
        },
      ];

    case "respiratory-rate":
      return [
        {
          id: "value",
          label: "Respiratory Rate",
          type: "number",
          placeholder: "Enter rate",
          unit: "BPM",
          required: true,
          min: 8,
          max: 60,
          step: 1,
        },
      ];

    default:
      return [
        {
          id: "value",
          label: "Value",
          type: "number",
          placeholder: "Enter value",
          required: true,
        },
      ];
  }
};

export const AddTrackerEntryModal = ({
  isOpen,
  onClose,
  metric,
  onSubmit,
  selectedPatient,
  onDataSubmitted,
  onDataSubmissionError,
  onValidationResponse,
  darkMode = false,
}: AddTrackerEntryModalProps & {
  selectedPatient?: User | null;
  onDataSubmitted?: () => void;
  onDataSubmissionError?: (errorMessage: string) => void;
  onValidationResponse?: (validation: ValidationResponse) => void;
  darkMode?: boolean;
}) => {
  const [selectedDate, setSelectedDate] = useState<Date>(new Date());
  const [selectedTime, setSelectedTime] = useState<string>("12:00");
  const [amPm, setAmPm] = useState<"AM" | "PM">("PM");
  const [hour, setHour] = useState<string>("12");
  const [minute, setMinute] = useState<string>("00");
  const [formData, setFormData] = useState<Record<string, any>>({});
  const [showCalendar, setShowCalendar] = useState(false);
  const [showTimePicker, setShowTimePicker] = useState(false);

  // Blood glucose source type states
  const [selectedGroup, setSelectedGroup] = useState<string>("");
  const [selectedSourceType, setSelectedSourceType] =
    useState<BloodGlucoseSourceType | null>(null);

  // Blood pressure states
  const [selectedPosition, setSelectedPosition] = useState<number | null>(null);
  const [selectedLocation, setSelectedLocation] = useState<number | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Temperature states
  const [temperatureLocations, setTemperatureLocations] = useState<
    Array<{ id: number; name: string; location: string }>
  >([]);
  const [selectedTemperatureLocationId, setSelectedTemperatureLocationId] =
    useState<number | null>(null);
  const [temperatureLocationsLoading, setTemperatureLocationsLoading] =
    useState(false);

  // Fetch blood glucose source types
  const {
    groupedAsOptions,
    getItemsByGroup,
    loading: sourceTypesLoading,
  } = useBloodGlucoseSourceTypes();

  // Fetch blood pressure data
  const { positions, loading: positionsLoading } = useBloodPressurePositions();
  const { locations, loading: locationsLoading } = useBloodPressureLocations();

  const fieldConfigs = metric ? getFieldConfigs(metric.id) : [];

  // Helper function to show validation snackbar via parent callback
  const showValidationSnackbar = (validation: ValidationResponse) => {
    if (onValidationResponse) {
      onValidationResponse(validation);
    }
  };

  // Initialize form data when modal opens
  useEffect(() => {
    if (isOpen && metric) {
      const currentFieldConfigs = getFieldConfigs(metric.id);
      const initialData: Record<string, any> = {};
      currentFieldConfigs.forEach((field) => {
        initialData[field.id] = "";
      });
      setFormData(initialData);

      // Reset blood glucose specific states
      if (metric.id === "blood-glucose") {
        setSelectedGroup("");
        setSelectedSourceType(null);
      }

      // Reset blood pressure specific states
      if (metric.id === "blood-pressure") {
        setSelectedPosition(null);
        setSelectedLocation(null);
      }

      // Reset temperature specific states
      if (metric.id === "temperature") {
        setSelectedTemperatureLocationId(null);
        setTemperatureLocations([]);
        // Fetch temperature locations
        (async () => {
          try {
            setTemperatureLocationsLoading(true);
            const resp = await apiService.getTemperatureLocations();
            setTemperatureLocations(resp.data ?? resp ?? []);
          } catch (e) {
            console.error("Failed to load temperature locations", e);
          } finally {
            setTemperatureLocationsLoading(false);
          }
        })();
      }

      // Set current date and time
      const now = new Date();
      setSelectedDate(now);

      const currentHour = now.getHours();
      const displayHour =
        currentHour === 0
          ? 12
          : currentHour > 12
            ? currentHour - 12
            : currentHour;
      const currentAmPm = currentHour >= 12 ? "PM" : "AM";

      setHour(displayHour.toString());
      setMinute(now.getMinutes().toString().padStart(2, "0"));
      setAmPm(currentAmPm);
      setSelectedTime(
        `${displayHour}:${now.getMinutes().toString().padStart(2, "0")}`
      );
    }
  }, [isOpen, metric?.id]);

  const handleInputChange = (fieldId: string, value: any) => {
    setFormData((prev) => ({
      ...prev,
      [fieldId]: value,
    }));
  };

  const handleTimeChange = (
    newHour: string,
    newMinute: string,
    newAmPm: "AM" | "PM"
  ) => {
    // Check if the new time combination would be in the future
    if (
      isCurrentDateSelected() &&
      isTimeInFuture(newHour, newMinute, newAmPm)
    ) {
      // Don't allow the change if it would result in a future time
      return;
    }

    setHour(newHour);
    setMinute(newMinute);
    setAmPm(newAmPm);
    setSelectedTime(`${newHour}:${newMinute}`);
  };

  // Check if a specific time is in the future for the selected date
  const isTimeInFuture = (
    checkHour: string,
    checkMinute: string,
    checkAmPm: "AM" | "PM"
  ) => {
    const now = new Date();
    const selectedDateTime = new Date(selectedDate);

    // Convert time to 24-hour format
    let hour24 = parseInt(checkHour);
    if (checkAmPm === "PM" && hour24 !== 12) {
      hour24 += 12;
    } else if (checkAmPm === "AM" && hour24 === 12) {
      hour24 = 0;
    }

    selectedDateTime.setHours(hour24, parseInt(checkMinute), 0, 0);

    return selectedDateTime > now;
  };

  // Check if a specific hour is valid considering current minute and AM/PM
  const isHourValid = (checkHour: string) => {
    if (!isCurrentDateSelected()) return true;

    // Check with current minute and AM/PM
    return !isTimeInFuture(checkHour, minute, amPm);
  };

  // Check if a specific minute is valid considering current hour and AM/PM
  const isMinuteValid = (checkMinute: string) => {
    if (!isCurrentDateSelected()) return true;

    // Check with current hour and AM/PM
    return !isTimeInFuture(hour, checkMinute, amPm);
  };

  // Check if a specific AM/PM is valid considering current hour and minute
  const isAmPmValid = (checkAmPm: "AM" | "PM") => {
    if (!isCurrentDateSelected()) return true;

    // Check with current hour and minute
    return !isTimeInFuture(hour, minute, checkAmPm);
  };

  // Check if current date is selected
  const isCurrentDateSelected = () => {
    const today = new Date();
    const selected = new Date(selectedDate);
    return selected.toDateString() === today.toDateString();
  };

  // Check if selected date/time is in the future
  const isDateTimeInFuture = () => {
    const now = new Date();
    const selectedDateTime = new Date(selectedDate);

    // Convert selected time to 24-hour format
    let hour24 = parseInt(hour);
    if (amPm === "PM" && hour24 !== 12) {
      hour24 += 12;
    } else if (amPm === "AM" && hour24 === 12) {
      hour24 = 0;
    }

    selectedDateTime.setHours(hour24, parseInt(minute), 0, 0);

    return selectedDateTime > now;
  };

  // Form validation function
  const isFormValid = () => {
    if (!metric) return false;

    // Check if all required fields are filled
    const requiredFields = fieldConfigs.filter((field) => field.required);
    for (const field of requiredFields) {
      if (!formData[field.id] || formData[field.id].toString().trim() === "") {
        return false;
      }
    }

    // Check if selected date/time is in the future
    if (isDateTimeInFuture()) {
      return false;
    }

    // Additional validation for blood glucose
    if (metric.id === "blood-glucose") {
      if (!selectedSourceType) return false;
    }

    // Additional validation for blood pressure
    if (metric.id === "blood-pressure") {
      if (!selectedPosition || !selectedLocation) return false;
    }

    // Additional validation for temperature
    if (metric.id === "temperature") {
      if (!selectedTemperatureLocationId) return false;
    }

    return true;
  };

  const handleSubmit = async () => {
    if (!metric || !selectedPatient) return;

    setIsSubmitting(true);

    try {
      // Convert time to 24-hour format
      let hour24 = parseInt(hour);
      if (amPm === "PM" && hour24 !== 12) {
        hour24 += 12;
      } else if (amPm === "AM" && hour24 === 12) {
        hour24 = 0;
      }

      // Handle blood pressure submission with new API
      if (metric.id === "blood-pressure") {
        const systolic = parseFloat(formData.systolic);
        const diastolic = parseFloat(formData.diastolic);

        if (!systolic || !diastolic || !selectedPosition || !selectedLocation) {
          console.error("Missing required blood pressure fields");
          return;
        }

        // Create ISO datetime string
        const readingDate = new Date(selectedDate);
        readingDate.setHours(hour24, parseInt(minute), 0, 0);

        // Validate the reading first
        try {
          const validationResponse =
            await apiService.validateBloodPressureReading({
              systolic_bp: systolic,
              diastolic_bp: diastolic,
              reading_datetime: readingDate.toISOString(),
            });

          const validation = validationResponse.data as ValidationResponse;

          // If validation returns error, show snackbar and don't save
          if (validation.category === "error") {
            showValidationSnackbar(validation);
            return;
          }

          // If validation passes (info or warning), save the data
          const bloodPressureData = {
            systolic_bp: systolic,
            diastolic_bp: diastolic,
            body_position: selectedPosition,
            location: selectedLocation,
            reading_datetime: readingDate.toISOString(),
            patient_id: selectedPatient.id,
          };

          await apiService.submitBloodPressureReading(bloodPressureData);

          // Show success or warning snackbar
          showValidationSnackbar(validation);

          // Call the callback to refresh the data
          if (onDataSubmitted) {
            onDataSubmitted();
          }

          onClose();
        } catch (error) {
          console.error("Blood pressure validation/submission error:", error);
          const errorValidation: ValidationResponse = {
            category: "error",
            title: "Error",
            description: "Failed to save blood pressure reading",
          };
          showValidationSnackbar(errorValidation);
        }
      } else if (metric.id === "blood-glucose") {
        // Handle blood glucose submission with new API
        const value = parseFloat(formData.value);

        if (!value || !selectedSourceType) {
          console.error("Missing required blood glucose fields");
          return;
        }

        // Create ISO datetime string
        const readingDate = new Date(selectedDate);
        readingDate.setHours(hour24, parseInt(minute), 0, 0);

        // Validate the reading first
        try {
          const validationResponse =
            await apiService.validateBloodGlucoseReading({
              value: value,
              sample_unit: "mg/dl",
              sample_type: selectedSourceType.id,
              reading_datetime: readingDate.toISOString(),
            });

          const validation = validationResponse.data as ValidationResponse;

          // If validation returns error, show snackbar and don't save
          if (validation.category === "error") {
            showValidationSnackbar(validation);
            return;
          }

          // If validation passes (info or warning), save the data
          const bloodGlucoseData = {
            value: value,
            sample_unit: "mg/dl",
            sample_type: selectedSourceType.id,
            reading_datetime: readingDate.toISOString(),
            patient_id: selectedPatient.id,
          };

          await apiService.submitBloodGlucoseReading(bloodGlucoseData);

          // Show success or warning snackbar
          showValidationSnackbar(validation);

          // Call the callback to refresh the data
          if (onDataSubmitted) {
            onDataSubmitted();
          }

          onClose();
        } catch (error) {
          console.error("Blood glucose validation/submission error:", error);
          const errorValidation: ValidationResponse = {
            category: "error",
            title: "Error",
            description: "Failed to save blood glucose reading",
          };
          showValidationSnackbar(errorValidation);
        }
      } else if (metric.id === "heart-rate") {
        // Handle heart rate submission with new API
        const rateValue = parseFloat(formData.value);

        if (!rateValue) {
          console.error("Missing required heart rate field");
          return;
        }

        // Create ISO datetime string
        const readingDate = new Date(selectedDate);
        readingDate.setHours(hour24, parseInt(minute), 0, 0);

        // Validate the reading first
        try {
          const validationResponse = await apiService.validateHeartRateReading({
            rate_value: rateValue,
            reading_datetime: readingDate.toISOString(),
          });

          const validation = validationResponse.data as ValidationResponse;

          // If validation returns error, show snackbar and don't save
          if (validation.category === "error") {
            showValidationSnackbar(validation);
            return;
          }

          // If validation passes (info or warning), save the data
          const heartRateData = {
            rate_value: rateValue,
            reading_datetime: readingDate.toISOString(),
            patient_id: selectedPatient.id,
          };

          await apiService.submitHeartRateReading(heartRateData);

          // Show success or warning snackbar
          showValidationSnackbar(validation);

          // Call the callback to refresh the data
          if (onDataSubmitted) {
            onDataSubmitted();
          }

          onClose();
        } catch (error) {
          console.error("Heart rate validation/submission error:", error);
          const errorValidation: ValidationResponse = {
            category: "error",
            title: "Error",
            description: "Failed to save heart rate reading",
          };
          showValidationSnackbar(errorValidation);
        }
      } else if (metric.id === "temperature") {
        // Handle temperature submission without validation
        const temperatureValue = parseFloat(formData.value);
        if (!temperatureValue || !selectedTemperatureLocationId) {
          console.error("Missing required temperature fields");
          return;
        }

        const readingDate = new Date(selectedDate);
        readingDate.setHours(hour24, parseInt(minute), 0, 0);

        try {
          const temperatureData = {
            temperature: temperatureValue,
            unit: "F" as const,
            location: selectedTemperatureLocationId,
            reading_datetime: readingDate.toISOString(),
            patient_id: selectedPatient.id,
          };

          await apiService.submitTemperatureReading(temperatureData);

          // Show success snackbar
          const successValidation: ValidationResponse = {
            category: "info",
            title: "Temperature reading saved",
            description: `Your temperature reading of ${temperatureValue}°F was recorded.`,
          };
          showValidationSnackbar(successValidation);

          if (onDataSubmitted) {
            onDataSubmitted();
          }

          onClose();
        } catch (error) {
          console.error("Temperature submission error:", error);
          const errorValidation: ValidationResponse = {
            category: "error",
            title: "Error",
            description: "Failed to save temperature reading",
          };
          showValidationSnackbar(errorValidation);
        }
      } else if (metric.id === "waist-size") {
        // Handle waist size submission with new API
        const waistSize = parseFloat(formData.value);

        if (!waistSize) {
          console.error("Missing required waist size field");
          return;
        }

        // Create ISO datetime string
        const readingDate = new Date(selectedDate);
        readingDate.setHours(hour24, parseInt(minute), 0, 0);

        // Validate the reading first
        try {
          const validationResponse = await apiService.validateWaistSizeReading({
            waist_size: waistSize,
            reading_datetime: readingDate.toISOString(),
            patient_id: selectedPatient.id,
          });

          const validation = validationResponse.data as ValidationResponse;

          // If validation returns error, show snackbar and don't save
          if (validation.category === "error") {
            showValidationSnackbar(validation);
            return;
          }

          // If validation passes (info or warning), save the data
          const waistSizeData = {
            waist_size: waistSize,
            reading_datetime: readingDate.toISOString(),
            patient_id: selectedPatient.id,
          };

          await apiService.submitWaistSizeReading(waistSizeData);

          // Show success or warning snackbar
          showValidationSnackbar(validation);

          // Call the callback to refresh the data
          if (onDataSubmitted) {
            onDataSubmitted();
          }

          onClose();
        } catch (error) {
          console.error("Waist size validation/submission error:", error);
          const errorValidation: ValidationResponse = {
            category: "error",
            title: "Error",
            description: "Failed to save waist size reading",
          };
          showValidationSnackbar(errorValidation);
        }
      } else if (metric.id === "weight") {
        // Handle weight submission with new API
        const weight = parseFloat(formData.value);

        if (!weight) {
          console.error("Missing required weight field");
          return;
        }

        // Create ISO datetime string
        const readingDate = new Date(selectedDate);
        readingDate.setHours(hour24, parseInt(minute), 0, 0);

        // Validate the reading first
        try {
          const validationResponse = await apiService.validateWeightReading({
            weight: weight,
            reading_datetime: readingDate.toISOString(),
            patient_id: selectedPatient.id,
          });

          const validation = validationResponse.data as ValidationResponse;

          // If validation returns error, show snackbar and don't save
          if (validation.category === "error") {
            showValidationSnackbar(validation);
            return;
          }

          // If validation passes (info or warning), save the data
          const weightData = {
            weight: weight,
            reading_datetime: readingDate.toISOString(),
            patient_id: selectedPatient.id,
          };

          await apiService.submitWeightReading(weightData);

          // Show success or warning snackbar
          showValidationSnackbar(validation);

          // Call the callback to refresh the data
          if (onDataSubmitted) {
            onDataSubmitted();
          }

          onClose();
        } catch (error) {
          console.error("Weight validation/submission error:", error);
          const errorValidation: ValidationResponse = {
            category: "error",
            title: "Error",
            description: "Failed to save weight reading",
          };
          showValidationSnackbar(errorValidation);
        }
      } else if (metric.id === "hip") {
        // Handle hip size submission with new API
        const hipSize = parseFloat(formData.value);

        if (!hipSize) {
          console.error("Missing required hip size field");
          return;
        }

        // Create ISO datetime string
        const readingDate = new Date(selectedDate);
        readingDate.setHours(hour24, parseInt(minute), 0, 0);

        // Validate the reading first
        try {
          const validationResponse = await apiService.validateHipSizeReading({
            hip_size: hipSize,
            reading_datetime: readingDate.toISOString(),
            patient_id: selectedPatient.id,
          });

          const validation = validationResponse.data as ValidationResponse;

          // If validation returns error, show snackbar and don't save
          if (validation.category === "error") {
            showValidationSnackbar(validation);
            return;
          }

          // If validation passes (info or warning), save the data
          const hipSizeData = {
            hip_size: hipSize,
            reading_datetime: readingDate.toISOString(),
            patient_id: selectedPatient.id,
          };

          await apiService.submitHipSizeReading(hipSizeData);

          // Show success or warning snackbar
          showValidationSnackbar(validation);

          // Call the callback to refresh the data
          if (onDataSubmitted) {
            onDataSubmitted();
          }

          onClose();
        } catch (error) {
          console.error("Hip size validation/submission error:", error);
          const errorValidation: ValidationResponse = {
            category: "error",
            title: "Error",
            description: "Failed to save hip size reading",
          };
          showValidationSnackbar(errorValidation);
        }
      } else if (metric.id === "respiratory-rate") {
        // Handle respiratory rate submission without validation
        const rateValue = parseFloat(formData.value);

        if (!rateValue) {
          console.error("Missing required respiratory rate field");
          return;
        }

        // Create ISO datetime string
        const readingDate = new Date(selectedDate);
        readingDate.setHours(hour24, parseInt(minute), 0, 0);

        try {
          const respiratoryRateData = {
            rate_value: rateValue,
            reading_datetime: readingDate.toISOString(),
            patient_id: selectedPatient.id,
          };

          await apiService.submitRespiratoryRateReading(respiratoryRateData);

          // Show success snackbar
          const successValidation: ValidationResponse = {
            category: "info",
            title: "Respiratory rate saved",
            description: `Your respiratory rate of ${rateValue} BPM was recorded.`,
          };
          showValidationSnackbar(successValidation);

          // Call the callback to refresh the data
          if (onDataSubmitted) {
            onDataSubmitted();
          }

          onClose();
        } catch (error) {
          console.error("Respiratory rate submission error:", error);
          const errorValidation: ValidationResponse = {
            category: "error",
            title: "Error",
            description: "Failed to save respiratory rate reading",
          };
          showValidationSnackbar(errorValidation);
        }
      } else {
        // Handle other metrics with existing format
        const submitData: TrackerEntryForm = {
          date: selectedDate,
          time: `${hour24.toString().padStart(2, "0")}:${minute}`,
          ...formData,
        };

        onSubmit(submitData);

        // Call the callback to refresh the data for other metrics too
        if (onDataSubmitted) {
          onDataSubmitted();
        }

        onClose();
      }
    } catch (error) {
      console.error("Error submitting tracker entry:", error);
      const errorValidation: ValidationResponse = {
        category: "error",
        title: "Error",
        description: "Failed to save data",
      };
      showValidationSnackbar(errorValidation);

      // Call error callback if provided
      if (onDataSubmissionError) {
        onDataSubmissionError("Failed to save data");
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCancel = () => {
    setFormData({});
    setShowCalendar(false);
    setShowTimePicker(false);

    // Reset blood glucose specific states
    setSelectedGroup("");
    setSelectedSourceType(null);

    // Reset blood pressure specific states
    setSelectedPosition(null);
    setSelectedLocation(null);

    onClose();
  };

  const renderField = (field: TrackerFieldConfig) => {
    switch (field.type) {
      case "number":
        return (
          <Input
            type="number"
            placeholder={field.placeholder}
            value={formData[field.id] || ""}
            onChange={(e) => handleInputChange(field.id, e.target.value)}
            min={field.min}
            max={field.max}
            step={field.step}
            className="w-full"
          />
        );

      case "text":
        return (
          <Input
            type="text"
            placeholder={field.placeholder}
            value={formData[field.id] || ""}
            onChange={(e) => handleInputChange(field.id, e.target.value)}
            className="w-full"
          />
        );

      case "select":
        return (
          <Select
            value={formData[field.id] || ""}
            onValueChange={(value) => handleInputChange(field.id, value)}
          >
            <SelectTrigger className="w-full">
              <SelectValue placeholder="Select option" />
            </SelectTrigger>
            <SelectContent>
              {field.options?.map((option) => (
                <SelectItem key={option.value} value={option.value}>
                  {option.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        );

      default:
        return null;
    }
  };

  if (!metric) return null;

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-md p-0 gap-0 flex flex-col border-border/60">
        <DialogHeader className="p-6 pb-0 shrink-0">
          <DialogTitle className="z-h2 text-foreground">
            Add {metric.title}
          </DialogTitle>
        </DialogHeader>
        <Separator className="mt-4" />

        <div className="px-6 py-4 space-y-4 overflow-y-auto">
          {/* Date Selection */}
          <div className="space-y-2">
            <Label className="z-label text-foreground">Select Date</Label>
            <Popover
              open={showCalendar}
              onOpenChange={setShowCalendar}
              modal={true}
            >
              <PopoverTrigger asChild>
                <Button
                  variant="outline"
                  className={cn(
                    "w-full justify-start text-left font-normal",
                    !selectedDate && "text-muted-foreground"
                  )}
                >
                  <CalendarIcon className="mr-2 h-4 w-4" />
                  {selectedDate
                    ? format(selectedDate, "EEE, dd MMMM")
                    : "Select Date"}
                </Button>
              </PopoverTrigger>
              <PopoverContent
                className="w-auto p-0"
                align="start"
                onOpenAutoFocus={(e) => e.preventDefault()}
              >
                <Calendar
                  mode="single"
                  selected={selectedDate}
                  onSelect={(date) => {
                    if (date) {
                      setSelectedDate(date);
                      setShowCalendar(false);
                    }
                  }}
                  disabled={(date) =>
                    date > new Date() || date < new Date("1900-01-01")
                  }
                  captionLayout="dropdown"
                  startMonth={new Date(new Date().getFullYear() - 5, 0)}
                  endMonth={new Date(new Date().getFullYear(), 11)}
                />
              </PopoverContent>
            </Popover>
          </div>

          {/* Time Selection */}
          <div className="space-y-2">
            <Label className="z-label text-foreground">Select Time</Label>
            <Popover
              open={showTimePicker}
              onOpenChange={setShowTimePicker}
              modal={true}
            >
              <PopoverTrigger asChild>
                <Button
                  variant="outline"
                  className="w-full justify-start text-left font-normal"
                >
                  <Clock className="mr-2 h-4 w-4" />
                  {`${selectedTime} ${amPm}`}
                </Button>
              </PopoverTrigger>
              <PopoverContent
                className="w-auto p-0"
                align="start"
                onOpenAutoFocus={(e) => e.preventDefault()}
              >
                <div className="p-4 space-y-4">
                  <div className="flex space-x-2">
                    {/* Hour */}
                    <div className="flex flex-col space-y-1">
                      <div className="z-caption text-center text-muted-foreground">
                        Hour
                      </div>
                      <Select
                        value={hour}
                        onValueChange={(value) =>
                          handleTimeChange(value, minute, amPm)
                        }
                      >
                        <SelectTrigger className="w-16">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent position="popper" className="z-[999]">
                          {Array.from({ length: 12 }, (_, i) => i + 1).map(
                            (h) => {
                              const hourStr = h.toString();
                              const isDisabled = !isHourValid(hourStr);
                              return (
                                <SelectItem
                                  key={h}
                                  value={hourStr}
                                  disabled={isDisabled}
                                  className={
                                    isDisabled
                                      ? "opacity-50 cursor-not-allowed"
                                      : ""
                                  }
                                >
                                  {hourStr.padStart(2, "0")}
                                </SelectItem>
                              );
                            }
                          )}
                        </SelectContent>
                      </Select>
                    </div>

                    {/* Minute */}
                    <div className="flex flex-col space-y-1">
                      <div className="z-caption text-center text-muted-foreground">
                        Min
                      </div>
                      <Select
                        value={minute}
                        onValueChange={(value) =>
                          handleTimeChange(hour, value, amPm)
                        }
                      >
                        <SelectTrigger className="w-16">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent position="popper" className="z-[999]">
                          {Array.from({ length: 12 }, (_, i) => i * 5).map(
                            (m) => {
                              const minuteStr = m.toString().padStart(2, "0");
                              const isDisabled = !isMinuteValid(minuteStr);
                              return (
                                <SelectItem
                                  key={m}
                                  value={minuteStr}
                                  disabled={isDisabled}
                                  className={
                                    isDisabled
                                      ? "opacity-50 cursor-not-allowed"
                                      : ""
                                  }
                                >
                                  {minuteStr}
                                </SelectItem>
                              );
                            }
                          )}
                        </SelectContent>
                      </Select>
                    </div>

                    {/* AM/PM */}
                    <div className="flex flex-col space-y-1">
                      <div className="z-caption text-center text-muted-foreground">
                        Period
                      </div>
                      <Select
                        value={amPm}
                        onValueChange={(value: "AM" | "PM") =>
                          handleTimeChange(hour, minute, value)
                        }
                      >
                        <SelectTrigger className="w-16">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent position="popper" className="z-[999]">
                          {["AM", "PM"].map((period) => {
                            const isDisabled = !isAmPmValid(
                              period as "AM" | "PM"
                            );
                            return (
                              <SelectItem
                                key={period}
                                value={period}
                                disabled={isDisabled}
                                className={
                                  isDisabled
                                    ? "opacity-50 cursor-not-allowed"
                                    : ""
                                }
                              >
                                {period}
                              </SelectItem>
                            );
                          })}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>

                  {isCurrentDateSelected() && isDateTimeInFuture() && (
                    <div className="z-caption text-destructive bg-destructive/10 p-2 rounded border border-destructive/20">
                      Cannot select future time for today
                    </div>
                  )}

                  <div className="flex justify-end space-x-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setShowTimePicker(false)}
                    >
                      Cancel
                    </Button>
                    <Button size="sm" onClick={() => setShowTimePicker(false)}>
                      Done
                    </Button>
                  </div>
                </div>
              </PopoverContent>
            </Popover>
          </div>

          {/* Future date/time warning */}
          {isDateTimeInFuture() && (
            <div className="z-caption text-destructive bg-destructive/10 p-3 rounded-lg border border-destructive/20">
              Cannot select future date or time. Please select a past or current
              date and time.
            </div>
          )}

          {/* Dynamic Fields */}
          {fieldConfigs.map((field) => (
            <div key={field.id} className="space-y-2">
              <Label className="z-label text-foreground">
                {field.label} {field.unit && `(${field.unit})`}
                {field.required && (
                  <span className="text-destructive ml-1">*</span>
                )}
              </Label>
              {renderField(field)}
            </div>
          ))}

          {/* Temperature Location */}
          {metric?.id === "temperature" && (
            <div className="space-y-2">
              <Label className="z-label text-foreground">
                Select location type
              </Label>
              <Select
                value={selectedTemperatureLocationId?.toString() || ""}
                onValueChange={(value) =>
                  setSelectedTemperatureLocationId(Number(value))
                }
                disabled={temperatureLocationsLoading}
              >
                <SelectTrigger className="w-full">
                  <SelectValue placeholder="Select location" />
                </SelectTrigger>
                <SelectContent>
                  {temperatureLocations.map((loc) => (
                    <SelectItem key={loc.id} value={loc.id.toString()}>
                      {loc.location}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}

          {/* Blood Glucose Reading Type */}
          {metric?.id === "blood-glucose" && (
            <>
              <div className="space-y-2">
                <Label className="z-label text-foreground">
                  Select reading type
                </Label>
                <Select
                  value={selectedGroup}
                  onValueChange={(value) => {
                    setSelectedGroup(value);
                    setSelectedSourceType(null);
                  }}
                  disabled={sourceTypesLoading}
                >
                  <SelectTrigger className="w-full">
                    <SelectValue placeholder="Select option" />
                  </SelectTrigger>
                  <SelectContent>
                    {groupedAsOptions.map((option) => (
                      <SelectItem key={option.value} value={option.value}>
                        {option.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {selectedGroup && (
                <div className="flex flex-wrap gap-2">
                  {getItemsByGroup(selectedGroup).map((item) => (
                    <Badge
                      key={item.id}
                      variant={
                        selectedSourceType?.id === item.id
                          ? "default"
                          : "outline"
                      }
                      className={cn(
                        "cursor-pointer h-9 px-4 transition-colors",
                        selectedSourceType?.id === item.id
                          ? "bg-primary text-primary-foreground hover:bg-primary/90"
                          : "text-muted-foreground border-border hover:bg-muted hover:text-foreground"
                      )}
                      onClick={() => setSelectedSourceType(item)}
                    >
                      {item.name}
                    </Badge>
                  ))}
                </div>
              )}
            </>
          )}

          {/* Blood Pressure Position and Location */}
          {metric?.id === "blood-pressure" && (
            <>
              <div className="space-y-2">
                <Label className="z-label text-foreground">Body Position</Label>
                <Select
                  value={selectedPosition?.toString() || ""}
                  onValueChange={(value) => setSelectedPosition(Number(value))}
                  disabled={positionsLoading}
                >
                  <SelectTrigger className="w-full">
                    <SelectValue placeholder="Select position" />
                  </SelectTrigger>
                  <SelectContent>
                    {positions.map((position) => (
                      <SelectItem
                        key={position.id}
                        value={position.id.toString()}
                      >
                        {position.display_name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label className="z-label text-foreground">Arm Location</Label>
                <Select
                  value={selectedLocation?.toString() || ""}
                  onValueChange={(value) => setSelectedLocation(Number(value))}
                  disabled={locationsLoading}
                >
                  <SelectTrigger className="w-full">
                    <SelectValue placeholder="Select location" />
                  </SelectTrigger>
                  <SelectContent>
                    {locations.map((location) => (
                      <SelectItem
                        key={location.id}
                        value={location.id.toString()}
                      >
                        {location.location}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </>
          )}
        </div>

        {/* Footer */}
        <Separator />
        <div className="flex justify-end gap-2 p-4">
          <Button variant="outline" onClick={handleCancel} className="px-6">
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={!isFormValid() || isSubmitting}
            className="px-6"
          >
            {isSubmitting ? "Submitting..." : "Submit"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};
